"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import {
  IconArrowRight,
  IconArrowUpRight,
  IconCheck,
} from "@/app/_components/icons";
import { subscribeToUpdates } from "@/app/actions/updates-subscription";
import { SUPPORT_MATRIX, formatMatrixDate } from "./photon-data";
import { PhotonPageMark } from "./photon-wordmark";
import {
  UPDATES,
  UPDATE_TYPES,
  UPDATE_TYPE_LABELS,
  versionGroups,
  type Update,
  type UpdateType,
} from "./updates-data";

const SUPPORT_URL = "/photon/support";
const CONTACT_URL = "mailto:contact@moondream.ai";

type TypeFilter = UpdateType | "any";

type Filters = {
  model: string;
  hardware: string;
  type: TypeFilter;
};

const DEFAULT_FILTERS: Filters = { model: "any", hardware: "any", type: "any" };

const uniqueSorted = (values: string[]) =>
  [...new Set(values)].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );

const MODEL_OPTIONS = uniqueSorted(UPDATES.flatMap((u) => u.models));
const HARDWARE_OPTIONS = uniqueSorted(UPDATES.flatMap((u) => u.hardware));

// Empty models/hardware on an entry means "applies to all", so it matches
// any concrete filter value.
const matches = (entry: Update, filters: Filters) =>
  (filters.model === "any" ||
    entry.models.length === 0 ||
    entry.models.includes(filters.model)) &&
  (filters.hardware === "any" ||
    entry.hardware.length === 0 ||
    entry.hardware.includes(filters.hardware)) &&
  (filters.type === "any" || entry.type === filters.type);

const supportRowLabel = (id: string) => {
  const row = SUPPORT_MATRIX.find((r) => r.id === id);
  return row ? `${row.model} on ${row.chip}` : id;
};

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  format,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  format?: (value: string) => string;
}) => (
  <label className="filter-field">
    <span className="filter-label mono">{label}</span>
    <select
      className="filter-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {format ? format(option) : option}
        </option>
      ))}
    </select>
  </label>
);

const SubscribeForm = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<
    { status: "idle" | "submitting" | "done" } | { status: "error"; message: string }
  >({ status: "idle" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState({ status: "submitting" });
    const result = await subscribeToUpdates(email);
    if (result.ok) {
      setState({ status: "done" });
    } else {
      setState({ status: "error", message: result.error ?? "Failed" });
    }
  };

  if (state.status === "done") {
    return (
      <p className="subscribe-done t-body-sm">
        <IconCheck size={14} /> Subscribed. Photon updates go to {email}.
      </p>
    );
  }

  return (
    <form className="subscribe-form" onSubmit={onSubmit}>
      <input
        type="email"
        className="subscribe-input"
        placeholder="you@company.com"
        aria-label="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <button
        type="submit"
        className="btn btn-secondary btn-sm"
        disabled={state.status === "submitting"}
      >
        {state.status === "submitting" ? "Subscribing…" : "Subscribe"}
      </button>
      {state.status === "error" ? (
        <span className="subscribe-error t-body-sm">{state.message}</span>
      ) : null}
    </form>
  );
};

const UpdateCard = ({ entry }: { entry: Update }) => (
  <article className="update-card" id={entry.id}>
    <div className="update-head">
      <span className="tag">{UPDATE_TYPE_LABELS[entry.type]}</span>
      <span className="update-version mono">v{entry.version}</span>
      <span className="update-date mono tabular">
        {formatMatrixDate(entry.date)}
      </span>
    </div>
    <h3 className="t-heading-md" style={{ marginTop: 12 }}>
      {entry.title}
    </h3>
    <p
      className="t-body-md"
      style={{ color: "var(--text-secondary)", marginTop: 8, maxWidth: 760 }}
    >
      {entry.impact}
    </p>

    <dl className="family-meta update-meta">
      <div>
        <dt>Models</dt>
        <dd>{entry.models.length > 0 ? entry.models.join(" · ") : "All models"}</dd>
      </div>
      <div>
        <dt>Hardware</dt>
        <dd>
          {entry.hardware.length > 0 ? entry.hardware.join(" · ") : "All hardware"}
        </dd>
      </div>
      {entry.benchmarkImpact ? (
        <div>
          <dt>Benchmarks</dt>
          <dd className="update-meta-wide">{entry.benchmarkImpact}</dd>
        </div>
      ) : null}
    </dl>

    {entry.knownLimitations ? (
      <div className="update-limits">
        <span className="update-limits-label mono">Known limitations</span>
        <ul>
          {entry.knownLimitations.map((limitation) => (
            <li key={limitation}>{limitation}</li>
          ))}
        </ul>
      </div>
    ) : null}

    {entry.links ? (
      <div className="update-links">
        {entry.links.docs ? (
          <a
            href={entry.links.docs}
            target="_blank"
            rel="noreferrer"
            className="family-cta"
          >
            Docs <IconArrowUpRight size={12} />
          </a>
        ) : null}
        {entry.links.supportRows?.map((rowId) => (
          <Link
            key={rowId}
            href={`${SUPPORT_URL}#${rowId}`}
            className="family-cta"
          >
            {supportRowLabel(rowId)} <IconArrowRight size={12} />
          </Link>
        ))}
        {entry.links.prs?.map((pr) => (
          <a
            key={pr.label}
            href={pr.href}
            target="_blank"
            rel="noreferrer"
            className="family-cta"
          >
            PR {pr.label} <IconArrowUpRight size={12} />
          </a>
        ))}
      </div>
    ) : null}
  </article>
);

export function UpdatesPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const filtered = useMemo(
    () => UPDATES.filter((entry) => matches(entry, filters)),
    [filters],
  );

  const groups = useMemo(() => versionGroups(filtered), [filtered]);

  return (
    <div className="md-landing">
      <Nav
        theme={theme}
        onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
      />

      <main>
        <section
          className="section"
          style={{ paddingTop: 72, paddingBottom: 48 }}
        >
          <div className="container">
            <PhotonPageMark section="Updates" />
            <h1
              className="t-display-md"
              style={{ marginTop: 12, maxWidth: 780 }}
            >
              Changes by release.
            </h1>
            <p
              className="t-body-lg"
              style={{
                color: "var(--text-secondary)",
                marginTop: 16,
                maxWidth: 680,
              }}
            >
              New model and hardware support, performance work, fixes, and
              compatibility changes.
            </p>

            <div className="follow-strip">
              <div className="follow-subscribe">
                <span className="filter-label mono">Get updates by email</span>
                <SubscribeForm />
              </div>
              <div className="follow-feeds">
                <span className="filter-label mono">Follow</span>
                <div className="follow-feed-links">
                  <a href="/photon/updates/feed.xml" className="family-cta">
                    RSS <IconArrowUpRight size={12} />
                  </a>
                  <a href="/photon/updates/feed.json" className="family-cta">
                    JSON feed <IconArrowUpRight size={12} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="filter-bar">
              <FilterSelect
                label="Model"
                value={filters.model}
                options={["any", ...MODEL_OPTIONS]}
                onChange={(v) => setFilters((f) => ({ ...f, model: v }))}
                format={(v) => (v === "any" ? "Any" : v)}
              />
              <FilterSelect
                label="Hardware"
                value={filters.hardware}
                options={["any", ...HARDWARE_OPTIONS]}
                onChange={(v) => setFilters((f) => ({ ...f, hardware: v }))}
                format={(v) => (v === "any" ? "Any" : v)}
              />
              <FilterSelect
                label="Change type"
                value={filters.type}
                options={["any", ...UPDATE_TYPES]}
                onChange={(v) =>
                  setFilters((f) => ({ ...f, type: v as TypeFilter }))
                }
                format={(v) =>
                  v === "any" ? "Any" : UPDATE_TYPE_LABELS[v as UpdateType]
                }
              />
              <span className="matrix-summary mono" style={{ margin: 0 }}>
                {filtered.length} of {UPDATES.length} updates
              </span>
            </div>

            {groups.length === 0 ? (
              <div className="honest-card" style={{ marginTop: 32 }}>
                <div
                  className="t-eyebrow"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  No matching updates
                </div>
                <p
                  className="t-body-md"
                  style={{ color: "var(--text-secondary)", marginTop: 10 }}
                >
                  No updates match these filters. To request support,{" "}
                  <a href={CONTACT_URL} className="link">
                    contact the design-partner team
                  </a>
                  .
                </p>
              </div>
            ) : (
              <div className="update-groups">
                {groups.map((group) => (
                  <div className="update-group" key={group.version}>
                    <div className="update-group-head">
                      <h2 className="t-heading-lg">Photon {group.version}</h2>
                      <span className="update-group-date mono tabular">
                        {formatMatrixDate(group.date)}
                      </span>
                    </div>
                    <div className="update-group-entries">
                      {group.entries.map((entry) => (
                        <UpdateCard key={entry.id} entry={entry} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="matrix-foot mono">
              <Link href={SUPPORT_URL}>Production support</Link> · feeds:{" "}
              <a href="/photon/updates/feed.xml">RSS</a> ·{" "}
              <a href="/photon/updates/feed.json">JSON</a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
